import React, { useEffect, useMemo, useState } from 'react'
import { Button, Collapse } from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'
import styled from 'styled-components/macro'
import useUserRole from 'src/hooks/useUserRole'
import { useVenueCustomRoles } from 'src/hooks/useIsBlocked'
import {
  getStockRoleAccessBullets,
  getStockRoleCapabilities,
} from 'src/utils/permissions'
import { getRoleColor, getRoleDisplayName, isVenueAdmin } from 'src/utils/roles'
import {
  readStockRoleAccessCollapsedChoice,
  stockRoleAccessStartsCollapsed,
  writeStockRoleAccessCollapsedChoice,
} from 'src/utils/stockRoleAccessCardState'

const Card = styled.div`
  margin-bottom: 12px;
  border: 1px solid #e8f4ff;
  background: linear-gradient(180deg, #f7fbff 0%, #ffffff 100%);
  border-radius: 8px;
  overflow: hidden;

  .ant-collapse {
    background: transparent;
    border: none;
  }

  .ant-collapse-item {
    border: none !important;
  }

  .ant-collapse-header {
    align-items: center !important;
    padding: 12px 40px 12px 14px !important;
  }

  .ant-collapse-content-box {
    padding: 0 14px 12px !important;
  }
`

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding-right: 8px;
`

const HeaderText = styled.div`
  min-width: 0;
`

const CardTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colours.otherBlack};
`

const CardIntro = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colours.oliveGrey};
  line-height: 1.4;
  margin-top: 2px;
`

const RoleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  color: #ffffff;
  background: ${({ $color }) => $color};
  white-space: nowrap;
  flex-shrink: 0;
`

const AccessList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const AccessItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colours.otherBlack};
`

const AccessLabel = styled.span`
  flex: 1;
  color: ${({ $allowed, theme }) =>
    $allowed ? theme.colours.otherBlack : theme.colours.oliveGrey};
`

const CardFooter = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #eef2f7;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
`

const FooterNote = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colours.oliveGrey};
  display: inline-flex;
  align-items: center;
  gap: 4px;
`

const StockRoleAccessCard = ({
  onViewAllPermissions,
  userRole: userRoleOverride,
}) => {
  const userRoleFromHook = useUserRole()
  const customRoles = useVenueCustomRoles()
  const userRole = userRoleOverride ?? userRoleFromHook
  const isAdmin = isVenueAdmin(userRole)
  const [activeKeys, setActiveKeys] = useState(() =>
    stockRoleAccessStartsCollapsed({
      storedCollapsed: readStockRoleAccessCollapsedChoice(),
      isAdmin: isVenueAdmin(userRoleOverride ?? userRoleFromHook),
    })
      ? []
      : ['access'],
  )

  useEffect(() => {
    if (readStockRoleAccessCollapsedChoice() !== null) return
    setActiveKeys(isVenueAdmin(userRole) ? [] : ['access'])
  }, [userRole])

  const { roleDisplayName } = useMemo(
    () => getStockRoleCapabilities(userRole, customRoles),
    [userRole, customRoles],
  )

  const accessBullets = useMemo(
    () => getStockRoleAccessBullets(userRole, customRoles),
    [userRole, customRoles],
  )

  const roleBadgeColor = getRoleColor(userRole)
  const roleLabel = roleDisplayName || getRoleDisplayName(userRole)
  const allowedCount = accessBullets.filter(item => item.allowed).length
  const isCollapsed = activeKeys.length === 0

  const handleCollapseChange = keys => {
    const nextKeys = Array.isArray(keys) ? keys : []
    setActiveKeys(nextKeys)
    writeStockRoleAccessCollapsedChoice(nextKeys.length === 0)
  }

  return (
    <Card data-testid='stock-permissions'>
      <Collapse
        activeKey={activeKeys}
        onChange={handleCollapseChange}
        bordered={false}
        expandIconPosition='right'
      >
        <Collapse.Panel
          key='access'
          header={
            <PanelHeader>
              <HeaderText>
                <CardTitle>Your access on Stock</CardTitle>
                {isCollapsed && isAdmin ? null : (
                  <CardIntro>
                    {isCollapsed
                      ? `${roleLabel} • ${allowedCount} of ${accessBullets.length} permissions`
                      : 'What you can do on this tab with your current role.'}
                  </CardIntro>
                )}
              </HeaderText>
              <RoleBadge $color={roleBadgeColor}>{roleLabel}</RoleBadge>
            </PanelHeader>
          }
        >
          <AccessList>
            {accessBullets.map(item => (
              <AccessItem key={item.key}>
                {item.allowed ? (
                  <CheckCircleOutlined
                    style={{ color: '#52c41a', marginTop: 2 }}
                  />
                ) : (
                  <CloseCircleOutlined
                    style={{ color: '#ff4d4f', marginTop: 2 }}
                  />
                )}
                <AccessLabel $allowed={item.allowed}>{item.label}</AccessLabel>
              </AccessItem>
            ))}
          </AccessList>

          <CardFooter>
            <FooterNote>
              <InfoCircleOutlined />
              Permissions apply to this venue only.
            </FooterNote>
            {typeof onViewAllPermissions === 'function' ? (
              <Button type='link' size='small' onClick={onViewAllPermissions}>
                View all role permissions
              </Button>
            ) : null}
          </CardFooter>
        </Collapse.Panel>
      </Collapse>
    </Card>
  )
}

export default StockRoleAccessCard
